import { type GridPaginationModel } from '@mui/x-data-grid-premium';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { cn } from 'cn';
import { useEffect, useMemo, useRef } from 'react';

import {
  type LedgerAccountsQueryParams,
  useQueryLedgerAccounts,
} from '~/data/ledgerAccount';

import {
  useUniversalFilterPayload,
  useUniversalFiltersSelector,
  useUniversalFiltersStore,
} from '~/modules/universalFilters';

import { DataTable } from '@vestigas/ui';

import { columns, type LedgerAccountRow } from './columns';

export function LedgerAccountsDataTable() {
  const navigate = useNavigate({
    from: '/settings/masterdata/ledger-accounts/',
  });
  const query = useSearch({
    from: '/(main)/settings/masterdata/ledger-accounts/',
  });

  const filterPayload = useUniversalFilterPayload<LedgerAccountsQueryParams>();
  const store = useUniversalFiltersStore();
  const selectedFilterId = useUniversalFiltersSelector(
    (context) => context.selectedFilterId,
  );

  const { limit, page } = query;
  const offset = page * limit;

  // Reset page to 0 when the effective filter payload changes (drawer edits,
  // search, save, reset). Skips the initial mount to avoid spurious navigation.
  const prevFilterPayloadRef = useRef<string | null>(null);
  const serializedFilterPayload = JSON.stringify(filterPayload);
  useEffect(() => {
    if (
      prevFilterPayloadRef.current !== null &&
      prevFilterPayloadRef.current !== serializedFilterPayload
    ) {
      void navigate({ search: (prev) => ({ ...prev, page: 0 }) });
    }

    prevFilterPayloadRef.current = serializedFilterPayload;
  }, [serializedFilterPayload, navigate]);

  const { data, isPending, isPlaceholderData } = useQueryLedgerAccounts({
    ...filterPayload,
    limit,
    offset,
  });

  const totalCount = data?.totalCount ?? data?.paginatedCount ?? 0;

  useEffect(() => {
    if (typeof totalCount !== 'number' || isPlaceholderData) {
      return;
    }

    store.trigger.setFilterResultCount({
      count: totalCount,
      filterId: selectedFilterId,
    });
  }, [isPlaceholderData, selectedFilterId, store, totalCount]);

  const paginationModel = useMemo<GridPaginationModel>(
    () => ({ page, pageSize: limit }),
    [page, limit],
  );

  function onPaginationModelChange(model: GridPaginationModel) {
    void navigate({
      search: (prev) => ({
        ...prev,
        limit: model.pageSize,
        page: model.page,
      }),
    });
  }

  return (
    <div className="size-full overflow-clip rounded-lg border border-tiga-border-lighter bg-white">
      <DataTable<LedgerAccountRow>
        columns={columns}
        getRowClassName={({ row }) =>
          cn(
            'cursor-pointer',
            row.isDeleted && '[&_.MuiDataGrid-cell]:text-tiga-text-lighter',
          )
        }
        getRowId={(row) => row.versionId}
        height="100%"
        loading={isPending}
        onPaginationModelChange={onPaginationModelChange}
        onRowClick={({ row }: { row: LedgerAccountRow }) =>
          void navigate({
            search: (prev) => ({
              ...prev,
              ledgerAccountId: row.id,
            }),
          })
        }
        pagination
        paginationModel={paginationModel}
        rowCount={totalCount}
        rowSelection={false}
        rows={data?.data ?? []}
      />
    </div>
  );
}
